<template>
    <div class="relative grid flex-1 grid-cols-1" ref="rootRef" data-tour="topbar-search">
        <form class="contents" @submit.prevent="runSearch">
            <input
                ref="inputRef"
                v-model="query"
                aria-label="Search"
                class="text-md col-start-1 row-start-1 block size-full bg-white pe-4 pl-12 text-base text-gray-900 outline-hidden placeholder:text-gray-400 xl:pl-16"
                name="search"
                placeholder="Type a code first — cust: 0300, prod: oak, sale: SA-3"
                type="search"
                autocomplete="off"
                @focus="openDropdown"
                @keydown.down.prevent="moveHighlight(1)"
                @keydown.up.prevent="moveHighlight(-1)"
                @keydown.enter.prevent="selectHighlighted"
                @keydown.esc="closeDropdown"
            />
            <MagnifyingGlassIcon
                aria-hidden="true"
                class="pointer-events-none col-start-1 row-start-1 ms-4 size-5 self-center text-gray-400 sm:size-6 xl:ms-6"
            />
        </form>

        <Teleport to="body">
            <div v-if="showDropdown" class="fixed inset-0 z-[400]" @click="closeDropdown" />
            <div
                v-if="showDropdown"
                class="fixed z-[410] overflow-hidden rounded-lg border border-gray-200 bg-white shadow-lg"
                :style="dropdownStyle"
            >
                <div class="border-b border-gray-100 bg-gray-50 px-4 py-2">
                    <template v-if="activeScope">
                        <p class="text-xs font-semibold text-gray-700">Searching {{ activeScope.label }}</p>
                        <p class="truncate text-[11px] text-gray-500">{{ activeScope.hint }}</p>
                    </template>
                    <template v-else>
                        <p class="text-xs font-semibold text-gray-700">Pick what to search</p>
                        <p class="text-[11px] text-gray-500">
                            One code, one list — <span class="font-mono">cust: 0300</span> looks at customers only.
                        </p>
                    </template>
                </div>

                <p v-if="message" class="bg-amber-50 px-4 py-2 text-[11px] text-amber-700">{{ message }}</p>

                <div v-if="loading" class="px-4 py-6 text-center text-sm text-gray-400">Searching...</div>

                <ul v-else-if="options.length" class="max-h-80 overflow-y-auto py-1" role="listbox">
                    <li
                        v-for="(option, index) in options"
                        :key="option.key"
                        role="option"
                        :aria-selected="index === highlightedIndex"
                        class="cursor-pointer px-4 py-2.5 hover:bg-gray-50"
                        :class="index === highlightedIndex ? 'bg-indigo-50' : ''"
                        @mousedown.prevent="chooseOption(option)"
                        @mouseenter="highlightedIndex = index"
                    >
                        <div v-if="option.type === 'scope'" class="flex items-center gap-3">
                            <span
                                class="shrink-0 rounded bg-gray-100 px-1.5 py-0.5 font-mono text-[11px] font-semibold text-gray-700"
                            >
                                {{ option.scope.code }}
                            </span>
                            <div class="min-w-0">
                                <p class="truncate text-sm font-medium text-gray-900">
                                    <template v-if="option.term">
                                        Search {{ option.scope.label }} for “{{ option.term }}”
                                    </template>
                                    <template v-else>{{ option.scope.label }}</template>
                                </p>
                                <p class="truncate text-xs text-gray-500">{{ option.scope.hint }}</p>
                            </div>
                        </div>

                        <div v-else class="flex items-start justify-between gap-3">
                            <div class="min-w-0">
                                <p class="truncate text-sm font-medium text-gray-900">{{ option.item.title }}</p>
                                <p v-if="option.item.subtitle" class="truncate text-xs text-gray-500">
                                    {{ option.item.subtitle }}
                                </p>
                            </div>
                            <span
                                class="shrink-0 rounded-full bg-gray-100 px-2 py-0.5 text-[11px] font-medium text-gray-600"
                            >
                                {{ option.item.label }}
                            </span>
                        </div>
                    </li>
                </ul>

                <div v-else class="px-4 py-6 text-center text-sm text-gray-400">{{ emptyText }}</div>

                <div class="flex items-center justify-between gap-3 border-t border-gray-100 px-4 py-2">
                    <p class="truncate text-[11px] text-gray-500">
                        Document numbers need no code — <span class="font-mono">SA-3</span>,
                        <span class="font-mono">PO-12</span>.
                    </p>
                    <button
                        type="button"
                        class="shrink-0 text-[11px] font-medium text-indigo-600 hover:underline"
                        @mousedown.prevent="openHelp"
                    >
                        How search works
                    </button>
                </div>
            </div>
        </Teleport>

        <ModalProductVariantDetails
            v-if="showVariantDetailsModal"
            :variant-id="variantDetailsId"
            @close-modal="closeVariantDetailsModal"
        />
    </div>
</template>

<script setup>
import { MagnifyingGlassIcon } from '@heroicons/vue/20/solid';
import { computed, nextTick, onMounted, onUnmounted, ref, watch } from 'vue';
import { useRouter } from 'vue-router';
import apiClient from '@/utils/axios.js';
import ModalProductVariantDetails from '@/components/Portal/ProductVariants/ModalProductVariantDetails.vue';

/**
 * A leading `cust:` names the list to search. Everything before the colon is a
 * code; the server owns the vocabulary (including aliases), so an unrecognised
 * one is still sent and answered rather than guessed at here.
 */
const CODE_PATTERN = /^([A-Za-z][A-Za-z_]*)\s*:/;
/** `SA-3`, `PO-12`, `SA-20260727-00001` — the prefix already names the list. */
const DOCUMENT_PATTERN = /^[A-Za-z]{2,5}-/;

/** Permission-shaped and static for the session, so it is fetched once. */
let cachedScopes = null;

const router = useRouter();

const query = ref('');
const results = ref([]);
const scopes = ref(cachedScopes ?? []);
const scopeKey = ref(null);
const message = ref(null);
const needsScope = ref(true);
const loading = ref(false);
const showDropdown = ref(false);
const highlightedIndex = ref(0);
const rootRef = ref(null);
const inputRef = ref(null);
const dropdownStyle = ref({ top: '0px', left: '0px', width: '320px' });
const showVariantDetailsModal = ref(false);
const variantDetailsId = ref(null);

let debounceTimer = null;
let requestSeq = 0;

/** @returns {{ code: string|null, term: string }} */
const parsed = computed(() => {
    const raw = query.value.trim();
    const matched = CODE_PATTERN.exec(raw);

    return matched
        ? { code: matched[1].toLowerCase(), term: raw.slice(matched[0].length).trim() }
        : { code: null, term: raw };
});

/** Whether the term names a list — anything else is answered from the guide, without a request. */
const isScoped = computed(() => parsed.value.code !== null || DOCUMENT_PATTERN.test(query.value.trim()));

const activeScope = computed(() => {
    const local = parsed.value.code
        ? scopes.value.find((scope) => scope.code === `${parsed.value.code}:`)
        : null;

    return local ?? scopes.value.find((scope) => scope.key === scopeKey.value) ?? null;
});

/**
 * The guide rows. Typing the start of a code or a list name narrows it; free
 * text keeps the whole list and carries the term into each row, so one click
 * turns "oak" into "prod: oak".
 */
const suggestions = computed(() => {
    const { code, term } = parsed.value;
    const rows = (carried) =>
        scopes.value.map((scope) => ({ key: `scope-${scope.key}`, type: 'scope', scope, term: carried }));

    if (code || !term) {
        return rows(term);
    }

    const lower = term.toLowerCase();
    const matched = scopes.value.filter(
        (scope) => scope.code.startsWith(lower) || scope.label.toLowerCase().startsWith(lower),
    );

    return matched.length
        ? matched.map((scope) => ({ key: `scope-${scope.key}`, type: 'scope', scope, term: '' }))
        : rows(term);
});

/** guide — teach the codes · prompt — a code is chosen, the term is too short · results — a real search. */
const view = computed(() => {
    if (!isScoped.value || needsScope.value) return 'guide';

    return parsed.value.term.length < 2 ? 'prompt' : 'results';
});

const options = computed(() => {
    if (view.value === 'guide') {
        return suggestions.value;
    }

    if (view.value === 'prompt') {
        return [];
    }

    return results.value.map((item, index) => ({
        key: `result-${item.entity}-${item.title}-${index}`,
        type: 'result',
        item,
    }));
});

const emptyText = computed(() => {
    if (view.value === 'prompt') {
        return activeScope.value
            ? `Type at least 2 characters after ${activeScope.value.code}`
            : 'Keep typing…';
    }

    if (!scopes.value.length && !isScoped.value) {
        return 'Search codes are unavailable right now — a document number such as SA-3 still works.';
    }

    if (activeScope.value) {
        return `No ${activeScope.value.label.toLowerCase()} match “${parsed.value.term}”`;
    }

    return `No results for “${parsed.value.term}”`;
});

const updateDropdownPosition = async () => {
    await nextTick();
    const el = rootRef.value;
    if (!el?.getBoundingClientRect) return;

    const rect = el.getBoundingClientRect();
    const maxWidth = Math.min(480, window.innerWidth - 16);
    const width = Math.min(Math.max(rect.width, Math.min(280, maxWidth)), maxWidth);
    const left = Math.max(8, Math.min(rect.left, window.innerWidth - width - 8));

    dropdownStyle.value = {
        top: `${Math.round(rect.bottom + 8)}px`,
        left: `${Math.round(left)}px`,
        width: `${Math.round(width)}px`,
    };
};

const closeDropdown = () => {
    showDropdown.value = false;
    highlightedIndex.value = 0;
};

const closeVariantDetailsModal = () => {
    showVariantDetailsModal.value = false;
    variantDetailsId.value = null;
};

const applyScopes = (list) => {
    if (!Array.isArray(list) || !list.length) return;
    cachedScopes = list;
    scopes.value = list;
};

/** Prime the guide from the server so it lists only the codes this user may use. */
const ensureScopes = async () => {
    if (cachedScopes) {
        scopes.value = cachedScopes;
        return;
    }

    try {
        const res = await apiClient.get('/dashboard/search', { params: { q: '' } });
        applyScopes(res.data?.scopes);
    } catch (error) {
        // Offline or blocked — the dropdown still explains the shape of a code.
    }
};

const openDropdown = async () => {
    showDropdown.value = true;
    await updateDropdownPosition();
    ensureScopes();
};

const runSearch = async () => {
    // Without a code there is nothing to query — the guide answers instead, and
    // no keystroke of a name or phone number reaches the database.
    if (!isScoped.value) {
        clearTimeout(debounceTimer);
        results.value = [];
        needsScope.value = true;
        scopeKey.value = null;
        loading.value = false;
        showDropdown.value = true;
        await updateDropdownPosition();
        return;
    }

    if (parsed.value.term.length < 2) {
        results.value = [];
        needsScope.value = false;
        showDropdown.value = true;
        await updateDropdownPosition();
        return;
    }

    const seq = ++requestSeq;
    loading.value = true;
    showDropdown.value = true;
    await updateDropdownPosition();

    try {
        const res = await apiClient.get('/dashboard/search', { params: { q: query.value.trim() } });
        if (seq !== requestSeq) return;

        results.value = res.data?.data ?? [];
        needsScope.value = Boolean(res.data?.needs_scope);
        message.value = res.data?.message ?? null;
        scopeKey.value = res.data?.scope ?? null;
        applyScopes(res.data?.scopes);
        highlightedIndex.value = 0;
    } catch (error) {
        if (seq !== requestSeq) return;
        results.value = [];
    } finally {
        if (seq === requestSeq) {
            loading.value = false;
        }
    }
};

const scheduleSearch = () => {
    clearTimeout(debounceTimer);
    debounceTimer = setTimeout(runSearch, 250);
};

watch(query, () => {
    message.value = null;
    highlightedIndex.value = 0;

    if (!isScoped.value) {
        clearTimeout(debounceTimer);
        results.value = [];
        needsScope.value = true;
        scopeKey.value = null;
        // Left open or closed as it was — the guide is already on screen while
        // the field has focus, and clearing the box after opening a result must
        // not pop it back up.
        if (showDropdown.value) {
            updateDropdownPosition();
        }
        return;
    }

    // A code is named, so the guide steps aside even before an answer comes back.
    needsScope.value = false;

    if (parsed.value.term.length < 2) {
        clearTimeout(debounceTimer);
        results.value = [];
        return;
    }

    scheduleSearch();
});

const moveHighlight = (delta) => {
    if (!showDropdown.value || !options.value.length) return;
    const total = options.value.length;
    highlightedIndex.value = (highlightedIndex.value + delta + total) % total;
};

/** Turn a guide row into a real search: keep what was typed, prepend its code. */
const applyScope = async (option) => {
    query.value = option.term ? `${option.scope.code} ${option.term}` : `${option.scope.code} `;
    highlightedIndex.value = 0;
    inputRef.value?.focus();

    // Let the query watcher settle first, then replace the debounce it queued —
    // choosing a list is deliberate, so it should not wait another beat.
    await nextTick();
    clearTimeout(debounceTimer);

    if (option.term) {
        runSearch();
    }
};

const openResult = (item) => {
    if (!item) return;

    closeDropdown();
    query.value = '';
    results.value = [];

    if (item.action === 'product_variant_modal') {
        const id = item.params?.id ?? item.id;
        if (!id) return;
        variantDetailsId.value = id;
        showVariantDetailsModal.value = true;
        return;
    }

    if (!item?.route?.name) return;

    router.push({
        name: item.route.name,
        params: item.route.params ?? {},
    });
};

const chooseOption = (option) => {
    if (!option) return;

    if (option.type === 'scope') {
        applyScope(option);
        return;
    }

    openResult(option.item);
};

const selectHighlighted = () => {
    if (!showDropdown.value || !options.value.length) {
        runSearch();
        return;
    }

    chooseOption(options.value[highlightedIndex.value]);
};

const openHelp = () => {
    closeDropdown();
    router.push({ name: 'help', query: { article: 'global_search' } });
};

const onWindowChange = () => {
    if (showDropdown.value) {
        updateDropdownPosition();
    }
};

onMounted(() => {
    window.addEventListener('resize', onWindowChange);
    window.addEventListener('scroll', onWindowChange, true);
});

onUnmounted(() => {
    clearTimeout(debounceTimer);
    window.removeEventListener('resize', onWindowChange);
    window.removeEventListener('scroll', onWindowChange, true);
});
</script>
